import { useMyPayments } from "api/Client/balance";
import InputSearch, {
  SelectInput,
} from "components/ClientLayoutPages/ClientInputField/InputsFilter";
import Container from "components/ClientLayoutPages/Container/Container";
import MyPaymentModal from "components/Modal/Client/MyPaymentModal/MyPaymentModal";
import SkeletonMyPayment from "components/SkeletonList/SkeletonMyPayment";
import { useState } from "react";
import {
  HiOutlineArrowLongLeft,
  HiOutlineArrowLongRight,
  HiOutlineBanknotes,
  HiOutlineCalendarDays,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineHashtag,
  HiOutlineIdentification,
  HiOutlineWallet,
  HiOutlineXCircle,
} from "react-icons/hi2";

const getStateMeta = (state) => {
  if (state === "pending") {
    return { label: "انتظار", icon: <HiOutlineClock />, color: "text-amber-400" };
  }
  if (state === "approved") {
    return { label: "مكتملة", icon: <HiOutlineCheckCircle />, color: "text-emerald-400" };
  }
  return { label: "مرفوضة", icon: <HiOutlineXCircle />, color: "text-rose-400" };
};

const getPaymentTypeMeta = (type) => {
  if (type === "bankNotice") {
    return { label: "إشعار بنكي", icon: <HiOutlineBanknotes className="text-orange-400" /> };
  }
  if (type === "balanceRecharge") {
    return { label: "شحن رصيد", icon: <HiOutlineWallet className="text-emerald-400" /> };
  }
  return { label: "- - - -", icon: <HiOutlineWallet className="text-slate-400" /> };
};

export default function MyPayments() {
  const [openMyPaymentModal, setOpenMyPaymentModal] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [orderState, setOrderState] = useState("");

  const {
    data: myPayments,
    isFetching: myPaymentsIsLoading,
    isSuccess: myPaymentsIsSuccess,
  } = useMyPayments(24, pageNumber, searchValue, orderState);

  return (
    <Container title="دفعاتي">
      <div className="portal-page-card mx-auto mb-8 mt-4 grid max-w-2xl grid-cols-1 gap-4 rounded-[1.5rem] p-4 md:grid-cols-2">
        <InputSearch
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          setPageNumber={setPageNumber}
        />
        <SelectInput
          value={orderState}
          setValue={setOrderState}
          setPageNumber={setPageNumber}
          options={[
            { value: "approved", label: "مكتمل" },
            { value: "rejected", label: "مرفوضة" },
            { value: "pending", label: "انتظار" },
          ]}
        />
      </div>

      {myPaymentsIsLoading ? <SkeletonMyPayment number={6} /> : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {!myPaymentsIsLoading &&
          myPaymentsIsSuccess &&
          myPayments.data?.myPayments.length > 0 &&
          myPayments.data.myPayments.map((item) => {
            const stateMeta = getStateMeta(item.state_notify);
            const typeMeta = getPaymentTypeMeta(item.order_type);

            return (
              <div
                key={item.id}
                onClick={() => {
                  setCurrentId(item.id);
                  setOpenMyPaymentModal(true);
                }}
                className="portal-page-card card-animation grid cursor-pointer gap-2.5 rounded-[1.35rem] p-4 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="flex items-center justify-between gap-2">
                  <div
                    className={`flex items-center gap-1.5 text-xs font-bold sm:text-sm ${stateMeta.color}`}
                  >
                    <span className="text-lg">{stateMeta.icon}</span>
                    {stateMeta.label}
                  </div>
                  <h2
                    className="text-lg font-bold portal-accent sm:text-xl"
                    style={{ direction: "ltr" }}
                  >
                    {Number(item.amount).toLocaleString()}{" "}
                    <span className="text-sm">
                      {item.order_type === "bankNotice"
                        ? item.currency_bank
                        : item.currency_customer}
                    </span>
                  </h2>
                </div>

                <InfoRow icon={typeMeta.icon} label="نوع العملية" value={typeMeta.label} />
                <InfoRow
                  icon={<HiOutlineIdentification className="text-amber-400" />}
                  label="رقم الإشعار"
                  value={item.notify_number}
                />
                <InfoRow
                  icon={<HiOutlineHashtag className="text-orange-300" />}
                  label="ملاحظات"
                  value={item.notes}
                  clamp
                />
                <InfoRow
                  icon={<HiOutlineCalendarDays className="text-orange-400" />}
                  label="التاريخ"
                  value={item.created_at}
                  ltr
                />
                <InfoRow
                  icon={<HiOutlineBanknotes className="text-emerald-400" />}
                  label="المبلغ المضاف"
                  value={`${Number(item.total_amount).toLocaleString()} ${item.currency_customer}`}
                  ltr
                />
              </div>
            );
          })}
      </div>

      <Pagination
        current={myPayments?.data?.pagination?.current_page || 0}
        total={myPayments?.data?.pagination?.last_page || 0}
        onPrev={() => setPageNumber((prev) => prev - 1)}
        onNext={() => setPageNumber((prev) => prev + 1)}
        disablePrev={pageNumber === 1 || myPaymentsIsLoading}
        disableNext={
          myPayments?.data?.myPayments.length === 0 ||
          pageNumber >= myPayments?.data.pagination?.last_page ||
          myPayments?.data?.pagination?.total === 0 ||
          myPaymentsIsLoading
        }
      />

      {openMyPaymentModal ? (
        <MyPaymentModal
          isOpen={openMyPaymentModal}
          toggle={setOpenMyPaymentModal}
          paymentId={currentId}
        />
      ) : null}
    </Container>
  );
}

function InfoRow({ icon, label, value, clamp = false, ltr = false }) {
  return (
    <div
      className="flex items-start gap-2.5"
      style={{ color: "var(--layout-text-muted)" }}
    >
      <div className="text-lg">{icon}</div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-xs font-semibold portal-accent">{label}</span>
        <span
          className={`text-sm font-bold sm:text-[0.95rem] ${clamp ? "line-clamp-2" : ""}`}
          style={{ direction: ltr ? "ltr" : "rtl", color: "var(--layout-text)" }}
        >
          {value}
        </span>
      </div>
    </div>
  );
}

function Pagination({ current, total, onPrev, onNext, disablePrev, disableNext }) {
  return (
    <div className="mt-16 flex w-full flex-col items-center">
      <span className="flex gap-1 text-sm portal-accent">
        <span className="font-semibold">{current}</span>
        <span className="font-medium">من أصل</span>
        <span className="font-semibold">{total}</span>
      </span>
      <div className="mt-3 flex gap-4">
        <button
          onClick={onPrev}
          disabled={disablePrev}
          className="portal-outline-button flex w-24 items-center justify-center gap-1 rounded-xl px-3 py-2 text-sm disabled:opacity-50"
        >
          <HiOutlineArrowLongRight />
          السابق
        </button>
        <button
          onClick={onNext}
          disabled={disableNext}
          className="portal-outline-button flex w-24 items-center justify-center gap-1 rounded-xl px-3 py-2 text-sm disabled:opacity-50"
        >
          التالي
          <HiOutlineArrowLongLeft />
        </button>
      </div>
    </div>
  );
}
